import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Breadcrumbs from '../components/BreadCrumbs';
import ChatbotComponent from '../components/ChatbotComponent';
import { getAISatelliteAssistance, addChatbotKnowledge } from '../services/chatbot';
import { getUserId } from '../helpers/local_storage_helpers';
import StyledHeadingText from '../components/Fonts/StyledHeadingText';
import Button from '../components/buttons/Button';

import { BiBrain, BiPlanet, BiBookOpen } from 'react-icons/bi';
import Dropdown from '../components/inputFields/DropDown';

const Chatbot = () => {
    const [activeTab, setActiveTab] = useState('chat');
    const [isLoading, setIsLoading] = useState(false);
    const [satelliteData, setSatelliteData] = useState(null);
    const [knowledgeForm, setKnowledgeForm] = useState({
        fact: '',
    });
    const [selectedFarmId, setSelectedFarmId] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState('en');

    const userId = getUserId();

    const handleSatelliteAssistance = async () => {
        if (!selectedFarmId) {
            toast.error('Please select a farm ID');
            return;
        }

        try {
            setIsLoading(true);
            const response = await getAISatelliteAssistance(selectedFarmId, selectedLanguage);
            if (response.status === 200) {
                setSatelliteData(response.data);
                toast.success('Satellite assistance data loaded successfully');
            }
        } catch (error) {
            console.error('Error getting satellite assistance:', error);
            toast.error('Failed to get satellite assistance data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddKnowledge = async (e) => {
        e.preventDefault();

        if (!knowledgeForm.fact.trim()) {
            toast.error('Please fill in all fields');
            return;
        }

        try {
            setIsLoading(true);
            const response = await addChatbotKnowledge({
                knowledge: knowledgeForm.fact,
            });

            if (response.status === 200) {
                toast.success('Knowledge added successfully');
                setKnowledgeForm({
                    fact: '',
                });
            }
        } catch (error) {
            console.error('Error adding knowledge:', error);
            toast.error('Failed to add knowledge');
        } finally {
            setIsLoading(false);
        }
    };

    const tabs = [
        { id: 'chat', label: 'Chat', icon: <BiBrain /> },
        { id: 'satellite', label: 'Satellite Assistance', icon: <BiPlanet /> },
        { id: 'knowledge', label: 'Add Knowledge', icon: <BiBookOpen /> }
    ];

    return (
        <div className="p-6">
            {/* Enhanced Header Section */}
            <div className="bg-gradient-to-r from-primary/10 to-green-50 rounded-xl p-6 border border-primary/20 mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <Breadcrumbs items={[{ name: 'Home', link: '/dashboard' }, { name: 'AI Assistant' }]} />
                        <div className="flex items-center gap-3 mt-3">
                            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg">
                                <BiBrain className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-800">AI Agricultural Assistant</h1>
                                <p className="text-gray-600 mt-1">Your intelligent farming companion with advanced AI capabilities</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-4 py-2">
                        <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-primary">AI Assistant Active</span>
                    </div>
                </div>
            </div>

            {/* Enhanced Tab Navigation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
                <div className="flex space-x-1 p-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 font-medium ${activeTab === tab.id
                                ? 'bg-white text-primary shadow-md border border-primary/30'
                                : 'text-gray-600 hover:text-gray-800 hover:bg-white hover:bg-opacity-50'
                                }`}
                        >
                            <span className={`text-lg ${activeTab === tab.id ? 'text-primary' : 'text-gray-500'}`}>
                                {tab.icon}
                            </span>
                            <span className="text-sm">{tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Enhanced Tab Content */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                {activeTab === 'chat' && (
                    <div className="h-[650px]">
                        <ChatbotComponent
                            userId={userId}
                        />
                    </div>
                )}

                {activeTab === 'satellite' && (
                    <div className="p-8">
                        {/* Satellite Section Header */}
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                                <BiPlanet className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">AI Satellite Assistance</h3>
                                <p className="text-sm text-gray-600">Get real-time satellite analysis for your farm</p>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-primary/10 to-green-50 border border-primary/20 rounded-xl p-6 mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        🗺️ Farm ID
                                    </label>
                                    <input
                                        type="text"
                                        value={selectedFarmId}
                                        onChange={(e) => setSelectedFarmId(e.target.value)}
                                        placeholder="Enter your farm ID..."
                                        className="w-full px-4 py-3 text-sm border border-primary/30 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200 bg-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        🌐 Language
                                    </label>
                                    <Dropdown
                                        options={[
                                            { label: '🇺🇸 English', value: 'en' },
                                            { label: '🇮🇳 Hindi', value: 'hi' },
                                            { label: '🇮🇳 Gujarati', value: 'gu' }
                                        ]}
                                        value={selectedLanguage}
                                        onChange={(e) => setSelectedLanguage(e)}
                                        className="w-full h-12"
                                    />
                                </div>
                            </div>

                            <Button
                                onClick={handleSatelliteAssistance}
                                disabled={isLoading || !selectedFarmId}
                                className="mt-6 bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 transition-all duration-200 flex items-center gap-2"
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Analyzing...
                                    </>
                                ) : (
                                    <>
                                        <BiPlanet className="w-5 h-5" />
                                        Get Satellite Analysis
                                    </>
                                )}
                            </Button>
                        </div>

                        {satelliteData && (
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-semibold text-gray-800">Satellite Analysis Results</h4>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <pre className="text-sm overflow-auto text-gray-700 leading-relaxed">
                                        {JSON.stringify(satelliteData, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'knowledge' && (
                    <div className="p-8">
                        {/* Knowledge Section Header */}
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                                <BiBookOpen className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">Add Chatbot Knowledge</h3>
                                <p className="text-sm text-gray-600">Enhance AI capabilities by adding new agricultural knowledge</p>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-primary/10 to-green-50 border border-primary/20 rounded-xl p-6">
                            <form onSubmit={handleAddKnowledge} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        📚 Agricultural Fact/Knowledge
                                    </label>
                                    <textarea
                                        value={knowledgeForm.fact}
                                        onChange={(e) => setKnowledgeForm(prev => ({ ...prev, fact: e.target.value }))}
                                        placeholder="Enter agricultural facts, best practices, or expert knowledge to improve AI responses..."
                                        className="w-full px-4 py-3 border border-primary/30 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200 bg-white"
                                        rows="6"
                                    />
                                    <p className="text-xs text-gray-500 mt-2">
                                        💡 Example: "Tomatoes need 6-8 hours of direct sunlight daily for optimal growth."
                                    </p>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isLoading || !knowledgeForm.fact.trim()}
                                    className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 transition-all duration-200 flex items-center gap-2"
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Adding Knowledge...
                                        </>
                                    ) : (
                                        <>
                                            <BiBookOpen className="w-5 h-5" />
                                            Add Knowledge to AI
                                        </>
                                    )}
                                </Button>
                            </form>
                        </div>

                        {/* Knowledge Tips */}
                        <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6">
                            <h4 className="text-lg font-semibold text-gray-800 mb-4">💡 Knowledge Enhancement Tips</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="text-blue-600 mb-2">🌱</div>
                                    <h5 className="font-semibold text-gray-800 mb-2">Crop Information</h5>
                                    <p className="text-sm text-gray-600">Add details about crop varieties, planting seasons, and growing conditions</p>
                                </div>
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                    <div className="text-amber-600 mb-2">🌦️</div>
                                    <h5 className="font-semibold text-gray-800 mb-2">Weather Patterns</h5>
                                    <p className="text-sm text-gray-600">Share regional weather insights and seasonal farming advice</p>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <div className="text-green-600 mb-2">🛡️</div>
                                    <h5 className="font-semibold text-gray-800 mb-2">Pest Control</h5>
                                    <p className="text-sm text-gray-600">Include organic and sustainable pest management techniques</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Chatbot; 